import { useState, useEffect, useMemo } from 'react';
import { settingsAPI } from '../services/api';
import { isDeactivatedStatus, deactivatedUserIds } from '../utils/userStatus';

/**
 * useTechOptions — shared hook for the field-tech picker.
 *
 * Fetches the tenant's user list (via settingsAPI.getUsers) and filters to
 * users whose `role === 'field_tech'` per the canonical `user_role.v1` seed
 * (see constants/vocabSeeds.js). Returns TypeaheadChips-shaped options.
 *
 * ─── Scope note (PT-22 W2) ──────────────────────────────────────────────
 * This hook is a READ over EXISTING users — it does NOT create new users
 * and is NOT the E5 deferral. E5 is the "+ Invite tech" CTA on the WO
 * create sheet (creating a new tech user from within WO create). That
 * remains omitted from W2 per plan.
 *
 * Mirrors `useSalespersonOptions` in shape and error handling. Difference:
 * salesperson uses a `can_sell` permission bit derived from the seed;
 * field_tech is a specific role identity (there is no `is_tech` bit — the
 * role IS the identity), so we filter on `role === 'field_tech'` directly.
 *
 * @param {object} [opts]
 * @param {boolean} [opts.enabled=true]
 *        Gate the fetch when the consumer knows it's not yet needed (e.g.,
 *        a closed slideover). Matches the useSalespersonOptions pattern.
 * @returns {{
 *   options: Array<{value: string, label: string, hint?: string}>,
 *   loading: boolean,
 *   users:   Array<any>
 * }}
 */
export default function useTechOptions({ enabled = true } = {}) {
  const [systemUsers, setSystemUsers] = useState([]);
  const [fieldTechs, setFieldTechs] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!enabled) return undefined;
    let ignore = false;
    setLoading(true);
    Promise.all([
      settingsAPI.getUsers().catch(() => []),
      // IDI-52 (B3): the migrated field_technician roster is the primary
      // assignment source for WO-related pickers (t270 has 36 migrated techs
      // vs. 4 Cognito users with role=='field_tech'). Cognito users remain a
      // fallback for net-new tenants that haven't migrated any techs.
      settingsAPI.getFieldTechnicians().catch(() => []),
    ])
      .then(([res, techsRes]) => {
        if (ignore) return;
        // Belt-and-suspenders normalization — matches useSalespersonOptions.
        const arr = Array.isArray(res) ? res
          : (Array.isArray(res?.data) ? res.data
            : (Array.isArray(res?.users) ? res.users : []));
        setSystemUsers(arr);
        const techArr = Array.isArray(techsRes) ? techsRes
          : (Array.isArray(techsRes?.data) ? techsRes.data : []);
        setFieldTechs(techArr);
      })
      .catch(() => { if (!ignore) { setSystemUsers([]); setFieldTechs([]); } })
      .finally(() => { if (!ignore) setLoading(false); });
    return () => { ignore = true; };
  }, [enabled]);

  const options = useMemo(() => {
    // IDI-52 (B3): migrated field techs first (id-keyed, already deduped by
    // the backend), then any Cognito field_tech-role users not already
    // represented — keeps net-new tenants working exactly as before.
    const byValue = new Map();
    (fieldTechs || []).forEach((t) => {
      const value = t.id || t.name;
      if (!value) return;
      byValue.set(String(value), {
        value,
        label: t.name || value,
        hint: undefined,
      });
    });
    (systemUsers || [])
      .filter((u) => {
        // IDI-681 D1: list_users reports a deactivated login as "inactive".
        if (isDeactivatedStatus(u.status)) return false;
        return (u.role || '').toLowerCase() === 'field_tech';
      })
      .forEach((u) => {
        const value = u.id || u.email || u.name;
        if (!value || byValue.has(String(value))) return;
        byValue.set(String(value), {
          // Option D dual-write: techs are ID-authoritative, unlike salespersons
          // which are name-authoritative. `value` is the stable ID that will
          // become `techIds[]` on the WO payload; `label` is the display name
          // that will become the `techs[]` name snapshot.
          value,
          label: u.name || u.email || u.id,
          hint: u.email && (u.name || u.id !== u.email) ? u.email : undefined,
        });
      });
    return Array.from(byValue.values());
  }, [systemUsers, fieldTechs]);

  const deactivatedIds = useMemo(() => deactivatedUserIds(systemUsers), [systemUsers]);

  return { options, loading, users: systemUsers, deactivatedIds };
}
